import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Film, Plus, RefreshCw, X, Loader2, Trash2, Save, ExternalLink, ChevronLeft, LayoutGrid, Rows3, Link2, Youtube, Search, Unlink, Sparkles, Archive, ArchiveRestore, ListChecks, Check } from "lucide-react";
import { API, authHeaders, fileUrl } from "@/constants/roles";
import { VideoPlayer } from "@/components/dashboard/VideoPlayer";
import { AdminYouTube } from "@/components/admin/AdminYouTube";
import { VideoAnalyticsPanel } from "@/components/admin/VideoAnalyticsPanel";
import { fmtMoney, fmtNum } from "@/utils/currency";

const STATUS_STYLES = {
  in_production: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  published: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};

const PERF_FIELDS = [
  { key: "views", label: "Views" },
  { key: "revenue", label: "Revenue ($)" },
  { key: "likes", label: "Likes" },
  { key: "comments", label: "Comments" },
  { key: "retention", label: "Avg viewed %" },
  { key: "stayed_to_watch", label: "Stayed to watch %" },
  { key: "subs_gained", label: "Subscribers gained" },
];

// synced-from-YouTube metric grid (linked productions). null = the official API does not
// expose the metric -> "Unavailable from YouTube"; a genuine 0 renders as 0.
const SYNCED_FIELDS = [
  { key: "views", label: "Views", fmt: (v) => fmtNum(v) },
  { key: "engaged_views", label: "Engaged views", fmt: (v) => fmtNum(v) },
  { key: "estimated_revenue", label: "Revenue", fmt: (v) => fmtMoney(v) },
  { key: "rpm", label: "RPM", fmt: (v) => fmtMoney(v, 4) },
  { key: "likes", label: "Likes", fmt: (v) => fmtNum(v) },
  { key: "comments", label: "Comments", fmt: (v) => fmtNum(v) },
  { key: "subs_gained", label: "Subscribers gained", fmt: (v) => fmtNum(v) },
  { key: "subs_lost", label: "Subscribers lost", fmt: (v) => fmtNum(v) },
  { key: "avg_view_duration_seconds", label: "Avg view duration", fmt: (v) => `${Math.round(v)}s` },
  { key: "avg_view_percentage", label: "Avg viewed %", fmt: (v) => `${Number(v).toFixed(1)}%` },
  { key: "watch_minutes", label: "Watch time (min)", fmt: (v) => fmtNum(Math.round(v)) },
  { key: "stayed_to_watch", label: "Stayed to watch %", fmt: (v) => `${v}%` },
];

const COST_FIELDS = [
  { key: "animator_base", label: "Animator base" },
  { key: "animator_bonus", label: "Animator bonus (syncs to payment)" },
  { key: "editing", label: "Editing" },
  { key: "script", label: "Script" },
  { key: "other", label: "Other" },
];

// Publish Pulse — live trend chips + mini sparkline for YouTube-linked productions.
// Deltas compare against the previous sync snapshot; nothing is shown until a second sync exists.
const TrendDelta = ({ current, previous, money = false }) => {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  const d = current - previous;
  if (!d) return null;
  const up = d > 0;
  return (
    <span className={`text-[9px] font-bold ${up ? "text-emerald-400" : "text-rose-400"}`} title="change since the last YouTube sync">
      {up ? "▲" : "▼"}{money ? fmtMoney(Math.abs(d), 2) : fmtNum(Math.abs(d))}
    </span>
  );
};

const Sparkline = ({ history, field, w = 56, h = 16 }) => {
  const pts = (history || []).map((p) => p[field]).filter((v) => v !== null && v !== undefined);
  if (pts.length < 2) return null;
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const step = w / (pts.length - 1);
  const coords = pts.map((v, i) => `${(i * step).toFixed(1)},${(max === min ? h / 2 : h - ((v - min) / (max - min)) * (h - 2) - 1).toFixed(1)}`).join(" ");
  const rising = pts[pts.length - 1] >= pts[0];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden="true" data-testid="pulse-sparkline">
      <polyline points={coords} fill="none" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" className={rising ? "stroke-emerald-400" : "stroke-rose-400"} />
    </svg>
  );
};

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
const labelCls = "mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]";

// Weekly hits digest — owner preview of the Monday email (computes fresh, sends nothing)
const DigestPreviewModal = ({ onClose }) => {
  const [digest, setDigest] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/hits-digest-preview`, { headers: authHeaders() })
      .then((r) => setDigest(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not build the digest."));
  }, []);
  return (
    <div data-testid="digest-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-[var(--pv-border)] p-5">
          <div>
            <p className="font-display text-sm font-bold text-[var(--pv-text)]">Weekly hits digest — preview</p>
            <p className="mt-0.5 text-[11px] text-[var(--pv-text-4)]">
              Exactly what Monday's email will highlight · computed fresh just now · nothing was sent
            </p>
          </div>
          <button data-testid="digest-close" onClick={onClose} aria-label="Close" className="rounded-full border border-[var(--pv-border)] p-1.5 text-[var(--pv-text-3)] transition-colors hover:text-[var(--pv-text)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {err ? (
            <p data-testid="digest-error" className="py-10 text-center text-sm text-rose-400">{err}</p>
          ) : !digest ? (
            <p data-testid="digest-loading" className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
          ) : digest.linked_count === 0 ? (
            <p data-testid="digest-empty" className="py-10 text-center text-sm text-[var(--pv-text-4)]">
              No linked productions yet — link videos from the Unlinked YouTube tab and the digest starts tracking after two syncs.
            </p>
          ) : (
            <div className="space-y-5">
              <p data-testid="digest-totals" className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3 text-xs font-semibold text-[var(--pv-text-3)]">
                {digest.tracked_count} linked production{digest.tracked_count === 1 ? "" : "s"} tracked this week · {fmtNum(digest.totals.views || 0)} total views
                {digest.totals.revenue != null && <> · {fmtMoney(digest.totals.revenue, 2)} total revenue</>}
              </p>
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--pv-text-4)]">Biggest view climbers</p>
                <div className="space-y-2">
                  {digest.top_views.filter((c) => (c.views_delta || 0) > 0).length === 0 ? (
                    <p data-testid="digest-no-climbers" className="rounded-2xl border border-dashed border-[var(--pv-border-strong)] px-4 py-4 text-center text-xs text-[var(--pv-text-4)]">
                      No upward movement this week — trends build as syncs accumulate.
                    </p>
                  ) : digest.top_views.filter((c) => (c.views_delta || 0) > 0).map((c, i) => (
                    <div key={c.video_id} data-testid={`digest-row-${c.video_id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[var(--pv-text)]">
                          <span className="mr-1.5 text-[10px] font-bold text-cyan-400">#{i + 1}</span>{c.title}
                        </p>
                        {c.animator_name && <p className="mt-0.5 text-[11px] text-[var(--pv-text-4)]">by {c.animator_name}</p>}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-bold text-emerald-400">+{fmtNum(c.views_delta)}</p>
                        <p className="text-[10px] text-[var(--pv-text-4)]">
                          {c.revenue_delta != null ? `+${fmtMoney(c.revenue_delta, 2)}` : "revenue n/a"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-[var(--pv-text-4)]">
                Sends automatically every Monday at 9:00 UTC to your inbox and bell. Deltas compare the latest sync with the sync nearest to 7 days ago.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LinkToProductionModal = ({ yt, readOnly, onClose, onDone }) => {  const [q, setQ] = useState("");
  const [cands, setCands] = useState(null);
  const [picked, setPicked] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      axios
        .get(`${API}/admin/youtube/linkable-videos?q=${encodeURIComponent(q)}&youtube_id=${yt.youtube_id}`, { headers: authHeaders() })
        .then((r) => setCands(r.data.videos || []))
        .catch(() => setCands([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, yt.youtube_id]);

  const link = async () => {
    if (!picked || linking) return;
    setLinking(true);
    try {
      await axios.post(`${API}/admin/videos/${picked.video_id}/link-youtube`, { youtube_id: yt.youtube_id }, { headers: authHeaders() });
      toast.success(`Linked "${yt.title}" to "${picked.public_title || picked.internal_title}".`);
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not link the video.");
      setLinking(false);
    }
  };

  const suggestedIds = new Set((yt.suggestions || []).map((s) => s.video_id));
  const rows = (cands || []).filter((c) => !suggestedIds.has(c.video_id));

  const candRow = (c, suggested) => (
    <button
      key={c.video_id}
      data-testid={`${suggested ? "link-suggestion" : "link-candidate"}-${c.video_id}`}
      onClick={() => setPicked(c)}
      className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${picked?.video_id === c.video_id ? "border-cyan-400 bg-cyan-500/10" : "border-[var(--pv-border)] bg-[var(--pv-raised)] hover:border-cyan-500/30"}`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-bold text-[var(--pv-text)]">{c.public_title || c.internal_title}</span>
        <span className="mt-0.5 block truncate text-[11px] text-[var(--pv-text-4)]">
          {c.internal_title && c.public_title ? `${c.internal_title} · ` : ""}
          {c.animator_name || "No animator"} · {(c.status || "").replace("_", " ")}
          {c.completion_date ? ` · completed ${new Date(c.completion_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
        </span>
      </span>
      {suggested && (
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
          <Sparkles className="h-3 w-3" /> Likely match
        </span>
      )}
    </button>
  );

  return (
    <div data-testid="link-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => !linking && onClose()}>
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-[var(--pv-border)] p-5">
          <div className="flex items-center gap-3">
            {yt.thumbnail && <img src={yt.thumbnail} alt="" className="h-14 w-24 rounded-xl object-cover" />}
            <div>
              <p className="font-display text-sm font-bold text-[var(--pv-text)]">{yt.title}</p>
              <p className="mt-0.5 text-[11px] text-[var(--pv-text-4)]">
                Published {yt.published_at ? new Date(yt.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"} · {fmtNum(yt.views || 0)} views
              </p>
            </div>
          </div>
          <button data-testid="link-modal-close" onClick={onClose} aria-label="Close" className="rounded-full border border-[var(--pv-border)] p-1.5 text-[var(--pv-text-3)] transition-colors hover:text-[var(--pv-text)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="border-b border-[var(--pv-border)] px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--pv-text-4)]" />
            <input
              data-testid="link-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search productions by title..."
              className="w-full rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] py-2.5 pl-9 pr-4 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-5">
          {!cands ? (
            <p className="py-10 text-center text-sm text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
          ) : (
            <>
              {(yt.suggestions || []).length > 0 && !q && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300/80">Likely matches — confirm to link, nothing is automatic</p>
                  {yt.suggestions.map((s) => candRow(s, true))}
                </div>
              )}
              {rows.length === 0 && (yt.suggestions || []).length === 0 ? (
                <p data-testid="link-empty" className="py-10 text-center text-sm text-[var(--pv-text-4)]">No unlinked productions match.</p>
              ) : rows.length > 0 && (
                <div className="space-y-2">
                  {!q && (yt.suggestions || []).length > 0 && <p className="pt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--pv-text-4)]">All unlinked productions</p>}
                  {rows.map((c) => candRow(c, false))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-[var(--pv-border)] p-5">
          <p className="text-[11px] text-[var(--pv-text-4)]">
            {picked ? <>Link to <span className="font-bold text-[var(--pv-text)]">{picked.public_title || picked.internal_title}</span>? The YouTube title becomes the display title; the internal title is kept for history.</> : "Select the production this upload belongs to."}
          </p>
          <button
            data-testid="link-confirm-btn"
            onClick={link}
            disabled={!picked || linking || readOnly}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-50"
          >
            {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Link to production
          </button>
        </div>
      </div>
    </div>
  );
};

export const AdminVideos = ({ openVideoId, onConsumeOpen, readOnly = false }) => {
  const [videos, setVideos] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [view, setView] = useState("cards");
  const [tab, setTab] = useState("productions");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workerFilter, setWorkerFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ internal_title: "", public_title: "", youtube_url: "", animator_id: "", editor_id: "", scriptwriter_id: "", production_notes: "" });
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [synced, setSynced] = useState([]);
  const [selSync, setSelSync] = useState(null);
  const [unlinked, setUnlinked] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showDigest, setShowDigest] = useState(false);
  const prevUnlinkedCount = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (tab === "archived") params.set("archived", "true");
    else if (statusFilter !== "all") params.set("status", statusFilter);
    if (workerFilter) params.set("worker_id", workerFilter);
    if (search.trim()) params.set("q", search.trim());
    axios.get(`${API}/admin/videos?${params}`, { headers: authHeaders() })
      .then((r) => setVideos(r.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load video library.");
      });
    axios.get(`${API}/admin/videos?archived=true`, { headers: authHeaders() })
      .then((r) => setArchivedCount((r.data || []).length))
      .catch(() => {});
    axios.get(`${API}/admin/team?include_archived=true`, { headers: authHeaders() }).then((r) => setWorkers(r.data)).catch(() => {});
    axios.get(`${API}/youtube/videos`, { headers: authHeaders() }).then((r) => setSynced(r.data.videos || [])).catch(() => setSynced([]));
    axios.get(`${API}/admin/youtube/unlinked`, { headers: authHeaders() })
      .then((r) => {
        const list = r.data.videos || [];
        if (prevUnlinkedCount.current !== null && list.length > prevUnlinkedCount.current) {
          toast.info(`${list.length - prevUnlinkedCount.current} new unlinked YouTube video${list.length - prevUnlinkedCount.current > 1 ? "s" : ""} detected — see the Unlinked YouTube tab.`);
        }
        prevUnlinkedCount.current = list.length;
        setUnlinked(list);
      })
      .catch(() => setUnlinked([]));
  }, [navigate, tab, statusFilter, workerFilter, search]);

  useEffect(load, [load]);

  const switchTab = (t) => {
    setTab(t);
    setStatusFilter(["published", "completed", "in_production"].includes(t) ? t : "all");
    setSelectMode(false);
    setSelectedIds([]);
  };

  const toggleSelect = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkArchive = async () => {
    const archiving = tab !== "archived";
    const ok = window.confirm(archiving
      ? `Archive ${selectedIds.length} production(s)? They leave the default views and linking lists, but status, costs, payments, links and history are all preserved. Restore anytime from the Archived tab.`
      : `Restore ${selectedIds.length} production(s) to the active workflow?`);
    if (!ok) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/admin/videos/bulk-archive`, { video_ids: selectedIds, archived: archiving }, { headers: authHeaders() });
      toast.success(archiving ? `${selectedIds.length} production(s) archived.` : `${selectedIds.length} production(s) restored.`);
      setSelectedIds([]);
      setSelectMode(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bulk action failed.");
    } finally { setBulkBusy(false); }
  };

  const openDetail = useCallback((id) => {
    setSelected(id);
    setDetail(null);
    axios.get(`${API}/admin/videos/${id}`, { headers: authHeaders() })
      .then((r) => {
        setDetail(r.data);
        const v = r.data;
        setEditForm({
          internal_title: v.internal_title || "", public_title: v.public_title || "",
          youtube_url: v.youtube_url || "", platform: v.platform || "YouTube",
          publish_date: (v.publish_date || "").slice(0, 10), status: v.status || "completed",
          animator_id: v.animator_id || "", editor_id: v.editor_id || "", scriptwriter_id: v.scriptwriter_id || "",
          production_notes: v.production_notes || "",
          costs: { animator_base: 0, animator_bonus: 0, editing: 0, script: 0, other: 0, ...(v.costs || {}) },
          performance: { views: 0, revenue: 0, likes: 0, comments: 0, retention: "", stayed_to_watch: "", subs_gained: 0, ...(v.performance || {}) },
        });
      })
      .catch(() => toast.error("Could not load video."));
  }, []);

  useEffect(() => {
    if (openVideoId) {
      openDetail(openVideoId);
      onConsumeOpen?.();
    }
  }, [openVideoId, openDetail, onConsumeOpen]);

  const createVideo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/videos`, {
        ...createForm,
        animator_id: createForm.animator_id || null,
        editor_id: createForm.editor_id || null,
        scriptwriter_id: createForm.scriptwriter_id || null,
      }, { headers: authHeaders() });
      toast.success("Video added to the library.");
      setShowCreate(false);
      setCreateForm({ internal_title: "", public_title: "", youtube_url: "", animator_id: "", editor_id: "", scriptwriter_id: "", production_notes: "" });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create video.");
    } finally { setSaving(false); }
  };

  const saveDetail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const perf = {};
      Object.entries(editForm.performance).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) perf[k] = v; });
      await axios.patch(`${API}/admin/videos/${selected}`, {
        internal_title: editForm.internal_title, public_title: editForm.public_title,
        youtube_url: editForm.youtube_url, platform: editForm.platform,
        publish_date: editForm.publish_date || null, status: editForm.status,
        animator_id: editForm.animator_id || null, editor_id: editForm.editor_id || null,
        scriptwriter_id: editForm.scriptwriter_id || null, production_notes: editForm.production_notes,
        costs: editForm.costs, performance: perf,
      }, { headers: authHeaders() });
      toast.success("Video updated.");
      openDetail(selected);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save video.");
    } finally { setSaving(false); }
  };

  const deleteVideo = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/videos/${selected}`, { headers: authHeaders() });
      toast.success("Video removed from the library.");
      setSelected(null);
      setDetail(null);
      setConfirmDelete(false);
      load();
    } catch { toast.error("Could not delete video."); } finally { setSaving(false); }
  };

  const archiveVideo = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const next = !detail.archived;
      await axios.post(`${API}/admin/videos/${selected}/archive`, { archived: next }, { headers: authHeaders() });
      toast.success(next ? "Production archived — hidden from default views, everything preserved." : "Production restored.");
      setConfirmArchive(false);
      openDetail(selected);
      load();
    } catch { toast.error("Could not update archive state."); } finally { setSaving(false); }
  };

  const unlinkVideo = async () => {    setSaving(true);
    try {
      await axios.post(`${API}/admin/videos/${selected}/unlink-youtube`, {}, { headers: authHeaders() });
      toast.success("YouTube link removed — the video is back in the Unlinked queue.");
      setConfirmUnlink(false);
      openDetail(selected);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not unlink.");
    } finally { setSaving(false); }
  };

  const workerSelect = (key, label) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select data-testid={`video-${key}-select`} value={editForm[key]} onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))} className={inputCls}>
        <option value="">— none —</option>
        {workers.map((w) => <option key={w.id} value={w.id}>{w.name || w.email}{w.worker_role ? ` (${w.worker_role})` : ""}</option>)}
      </select>
    </div>
  );

  if (selected) {
    const linked = Boolean(detail?.youtube_stats);
    return (
      <div data-testid="video-detail">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button data-testid="video-back-btn" onClick={() => { setSelected(null); setDetail(null); setConfirmDelete(false); setConfirmUnlink(false); setConfirmArchive(false); }} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]">
            <ChevronLeft className="h-3.5 w-3.5" /> Back to library
          </button>
          {!readOnly && detail && (confirmArchive ? (
            <span className="flex items-center gap-2 rounded-full border border-slate-500/30 bg-slate-500/10 px-3 py-1.5">
              <span className="text-[11px] font-bold text-slate-300">{detail.archived ? "Restore this production to the active workflow?" : "Archive it? Status, costs, payments, link and history are all preserved."}</span>
              <button data-testid="video-archive-confirm-btn" onClick={archiveVideo} className="rounded-full bg-slate-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-slate-600">{detail.archived ? "Yes, restore" : "Yes, archive"}</button>
              <button onClick={() => setConfirmArchive(false)} className="text-[11px] font-bold text-[var(--pv-text-3)]">Cancel</button>
            </span>
          ) : (
            <button data-testid="video-archive-btn" onClick={() => setConfirmArchive(true)} className="flex items-center gap-1.5 rounded-full bg-slate-500/10 border border-slate-500/30 px-4 py-2 text-xs font-bold text-slate-300 hover:bg-slate-500/20">
              {detail.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />} {detail.archived ? "Restore" : "Archive"}
            </button>
          ))}
          {!readOnly && (confirmDelete ? (
            <span className="flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1.5">
              <span className="text-[11px] font-bold text-rose-300">Delete this video record permanently?</span>
              <button data-testid="video-delete-confirm-btn" onClick={deleteVideo} className="rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-rose-600">Yes, delete</button>
              <button onClick={() => setConfirmDelete(false)} className="text-[11px] font-bold text-[var(--pv-text-3)]">Cancel</button>
            </span>
          ) : (
            <button data-testid="video-delete-btn" onClick={() => setConfirmDelete(true)} className="flex items-center gap-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 px-4 py-2 text-xs font-bold text-rose-400 hover:bg-rose-500/20">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          ))}
        </div>

        {!detail || !editForm ? (
          <p data-testid="video-loading" className="py-20 text-center text-sm font-medium text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
        ) : (
          <form data-testid="video-edit-form" onSubmit={saveDetail} className="mt-6 space-y-6">
          {detail.archived && (
            <div data-testid="video-archived-banner" className="flex items-center gap-2.5 rounded-3xl border border-slate-500/30 bg-slate-500/10 px-5 py-3.5 text-xs font-semibold text-slate-300">
              <Archive className="h-4 w-4 shrink-0" /> Archived — hidden from the default production workflow and linking lists. Status, costs, payments and history are fully preserved. Restore it anytime from here.
            </div>
          )}
          <fieldset disabled={readOnly} className="contents">
            {linked && (
              <div data-testid="youtube-link-panel" className="rounded-3xl border border-rose-500/25 bg-rose-500/5 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-rose-300">
                    <Youtube className="h-4 w-4" /> Linked to YouTube
                  </h3>
                  {!readOnly && (confirmUnlink ? (
                    <span className="flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1.5">
                      <span className="text-[11px] font-bold text-rose-300">Unlink? The video returns to the Unlinked queue; costs and history stay.</span>
                      <button type="button" data-testid="unlink-confirm-btn" onClick={unlinkVideo} className="rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-rose-600">Yes, unlink</button>
                      <button type="button" onClick={() => setConfirmUnlink(false)} className="text-[11px] font-bold text-[var(--pv-text-3)]">Cancel</button>
                    </span>
                  ) : (
                    <button type="button" data-testid="unlink-btn" onClick={() => setConfirmUnlink(true)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] hover:text-rose-300">
                      <Unlink className="h-3 w-3" /> Unlink
                    </button>
                  ))}
                </div>
                <a data-testid="linked-youtube-url" href={detail.youtube_url} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-2 text-xs font-semibold text-cyan-400 hover:text-cyan-300">
                  <ExternalLink className="h-3.5 w-3.5" /> {detail.youtube_url}
                </a>
                {(detail.youtube_stats?.history || []).length >= 2 && (
                  <div className="mt-3 flex items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2.5" data-testid="pulse-trend-strip">
                    <Sparkline history={detail.youtube_stats.history} field="views" w={120} h={28} />
                    <p className="text-[11px] font-semibold text-[var(--pv-text-4)]">
                      Views trend over the last {detail.youtube_stats.history.length} syncs
                      {detail.youtube_stats.previous?.synced_at ? ` · vs ${new Date(detail.youtube_stats.previous.synced_at).toLocaleString()}` : ""}
                    </p>
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="synced-perf-panel">
                  {SYNCED_FIELDS.map((f) => {
                    const val = detail.youtube_stats?.[f.key];
                    return (
                      <div key={f.key} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2.5">
                        <p className="text-[10px] font-semibold text-[var(--pv-text-4)]">{f.label}</p>
                        {val === null || val === undefined ? (
                          <p data-testid={`synced-${f.key}`} className="mt-0.5 text-xs font-medium italic text-[var(--pv-text-4)]">Unavailable from YouTube</p>
                        ) : (
                          <p data-testid={`synced-${f.key}`} className="mt-0.5 flex items-center gap-1.5 text-sm font-bold text-[var(--pv-text)]">
                            {f.fmt(val)}
                            {(f.key === "views" || f.key === "estimated_revenue") && (
                              <TrendDelta current={val} previous={detail.youtube_stats?.previous?.[f.key]} money={f.key === "estimated_revenue"} />
                            )}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] font-semibold text-rose-300/80">
                  Synced from YouTube{detail.youtube_stats?.synced_at ? ` · last sync ${new Date(detail.youtube_stats.synced_at).toLocaleString()}` : ""} — updates automatically on every sync. Production cost and payment records are never changed by syncing.
                </p>
              </div>
            )}
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-6">
                {detail.final_file_path && (
                  <VideoPlayer path={detail.final_file_path} filename="Final submission" testid="video-final-player" />
                )}
                {detail.youtube_url && !linked && (
                  <a data-testid="video-youtube-link" href={detail.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3 text-sm font-semibold text-cyan-400 hover:text-cyan-300">
                    <ExternalLink className="h-4 w-4" /> {detail.youtube_url}
                  </a>
                )}
                <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Identity</h3>
                  <div className="mt-4 space-y-4">
                    <div><label className={labelCls}>Internal title</label><input data-testid="video-internal-title" value={editForm.internal_title} onChange={(e) => setEditForm((f) => ({ ...f, internal_title: e.target.value }))} className={inputCls} /></div>
                    <div><label className={labelCls}>Final / public title{linked ? " (from YouTube)" : ""}</label><input data-testid="video-public-title" value={editForm.public_title} onChange={(e) => setEditForm((f) => ({ ...f, public_title: e.target.value }))} className={inputCls} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>Status</label>
                        <select data-testid="video-status-select" value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                          <option value="in_production">In production</option>
                          <option value="completed">Completed</option>
                          <option value="published">Published</option>
                        </select>
                      </div>
                      <div><label className={labelCls}>Publish date</label><input data-testid="video-publish-date" type="date" value={editForm.publish_date} onChange={(e) => setEditForm((f) => ({ ...f, publish_date: e.target.value }))} className={inputCls} /></div>
                    </div>
                    <div>
                      <label className={labelCls}>YouTube URL</label>
                      <input data-testid="video-url-input" value={editForm.youtube_url} onChange={(e) => setEditForm((f) => ({ ...f, youtube_url: e.target.value }))} placeholder="https://youtube.com/shorts/..." className={inputCls} disabled={linked} />
                      {linked && <p className="mt-1 text-[11px] text-[var(--pv-text-4)]">Managed by the YouTube link — unlink first to change it.</p>}
                    </div>
                  </div>
                </div>
                <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Contributors</h3>
                  <div className="mt-4 space-y-4">
                    {workerSelect("animator_id", "Animator")}
                    {workerSelect("editor_id", "Editor")}
                    {workerSelect("scriptwriter_id", "Scriptwriter")}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Performance{linked ? " (manual entries)" : ""}</h3>
                  {linked && <p className="mt-1 text-[11px] text-[var(--pv-text-4)]">Synced YouTube metrics are shown above. These manual fields stay available for offline platforms and owner corrections.</p>}
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {PERF_FIELDS.map((f) => (
                      <div key={f.key}>
                        <label className="mb-1 block text-[11px] font-semibold text-[var(--pv-text-3)]">{f.label}</label>
                        <input
                          data-testid={`perf-${f.key}`}
                          type="number" step="any" min="0"
                          value={editForm.performance[f.key] ?? ""}
                          onChange={(e) => setEditForm((x) => ({ ...x, performance: { ...x.performance, [f.key]: e.target.value === "" ? "" : parseFloat(e.target.value) } }))}
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>
                  {!linked && (detail.performance?.views > 0) && (
                    <p className="mt-3 text-xs text-[var(--pv-text-4)]">
                      Profit so far: <span className={`font-bold ${(detail.performance.revenue - (detail.costs?.total || 0)) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtMoney(detail.performance.revenue - (detail.costs?.total || 0))}</span>
                      {" "}(revenue {fmtMoney(detail.performance.revenue)} − cost {fmtMoney(detail.costs?.total || 0)})
                    </p>
                  )}
                </div>

                <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Production cost</h3>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {COST_FIELDS.map((f) => (
                      <div key={f.key}>
                        <label className="mb-1 block text-[11px] font-semibold text-[var(--pv-text-3)]">{f.label}</label>
                        <input
                          data-testid={`cost-${f.key}`}
                          type="number" step="0.01" min="0"
                          value={editForm.costs[f.key] ?? 0}
                          onChange={(e) => setEditForm((x) => ({ ...x, costs: { ...x.costs, [f.key]: parseFloat(e.target.value) || 0 } }))}
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-[var(--pv-text-4)]">Total: <span className="font-bold text-[var(--pv-text)]">${COST_FIELDS.reduce((s, f) => s + (editForm.costs[f.key] || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</span> · revisions: {detail.revision_count || 0}</p>
                </div>

                <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Production</h3>
                  {detail.job ? (
                    <div className="mt-3 rounded-2xl bg-[var(--pv-subtle)] p-4 text-xs text-[var(--pv-text-3)]">
                      <p className="font-bold text-[var(--pv-text)]">Linked job: {detail.job.title}</p>
                      <p className="mt-1">Status: {detail.job.status} · Pay locked: {fmtMoney(detail.job.estimated_pay)}{detail.job.claimed_role ? ` (${detail.job.claimed_role})` : ""}</p>
                      {detail.submissions?.length > 0 && <p className="mt-1">{detail.submissions.length} submission(s) — latest v{detail.submissions[0].version}</p>}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-[var(--pv-text-4)]">Not linked to a production job.</p>
                  )}
                  <label className={`${labelCls} mt-4`}>Production notes</label>
                  <textarea data-testid="video-notes-input" rows={3} value={editForm.production_notes} onChange={(e) => setEditForm((f) => ({ ...f, production_notes: e.target.value }))} className={inputCls} />
                </div>
              </div>
            </div>

            <VideoAnalyticsPanel youtubeUrl={detail.youtube_url} />

            {!readOnly && (
              <button data-testid="video-save-btn" type="submit" disabled={saving} className="flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 hover:bg-cyan-600 disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save video
              </button>
            )}
            {readOnly && <p className="text-xs text-[var(--pv-text-4)]">View only — only the owner can edit video records.</p>}
          </fieldset>
          </form>
        )}
      </div>
    );
  }

  const TABS = [
    { id: "productions", label: "Productions" },
    { id: "unlinked", label: "Unlinked YouTube", badge: unlinked.length },
    { id: "published", label: "Published" },
    { id: "completed", label: "Completed" },
    { id: "in_production", label: "In Production" },
    { id: "archived", label: "Archived", badge: archivedCount, badgeGray: true },
    { id: "youtube", label: "Full YouTube Library" },
  ];

  return (
    <div data-testid="admin-videos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Video Library</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Every production, linked to its job, crew, cost, and performance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-1">
            <button data-testid="videos-view-cards" onClick={() => setView("cards")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${view === "cards" ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)]"}`}><LayoutGrid className="h-3.5 w-3.5" /> Cards</button>
            <button data-testid="videos-view-table" onClick={() => setView("table")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${view === "table" ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)]"}`}><Rows3 className="h-3.5 w-3.5" /> Table</button>
          </div>
          <button data-testid="videos-refresh-btn" onClick={load} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)]"><RefreshCw className="h-3.5 w-3.5" /></button>
          {!readOnly && ["productions", "published", "completed", "in_production", "archived"].includes(tab) && (
            <button
              data-testid="videos-select-toggle"
              onClick={() => { setSelectMode((s) => !s); setSelectedIds([]); }}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${selectMode ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)]"}`}
            >
              <ListChecks className="h-3.5 w-3.5" /> {selectMode ? "Cancel" : "Select"}
            </button>
          )}
          {!readOnly && (
            <button data-testid="digest-preview-btn" onClick={() => setShowDigest(true)} className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/20">
              <Sparkles className="h-3.5 w-3.5" /> Preview digest
            </button>
          )}
          {!readOnly && (
            <button data-testid="new-video-btn" onClick={() => setShowCreate((s) => !s)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white shadow-md shadow-cyan-500/25 hover:bg-cyan-600">
              {showCreate ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showCreate ? "Close" : "Add video"}
            </button>
          )}
        </div>
      </div>

      <AdminYouTube readOnly={readOnly} onSynced={load} />

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="videos-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`videos-tab-${t.id}`} onClick={() => switchTab(t.id)} className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold ${tab === t.id ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)]"}`}>
            {t.id === "unlinked" && <Youtube className="h-3 w-3" />}
            {t.id === "archived" && <Archive className="h-3 w-3" />}
            {t.label}
            {t.badge > 0 && (
              <span data-testid={t.id === "unlinked" ? "unlinked-count-badge" : `${t.id}-count-badge`} className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${tab === t.id ? "bg-white text-cyan-600" : t.badgeGray ? "bg-slate-500 text-white" : "bg-rose-500 text-white"}`}>{t.badge}</span>
            )}
          </button>
        ))}
        {tab !== "unlinked" && tab !== "youtube" && (
          <>
            <select data-testid="videos-worker-filter" value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none">
              <option value="">All workers</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name || w.email}</option>)}
            </select>
            <input data-testid="videos-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search titles..." className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-1.5 text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none" />
          </>
        )}
      </div>

      {showCreate && tab !== "unlinked" && tab !== "youtube" && (
        <form data-testid="video-create-form" onSubmit={createVideo} className="mt-5 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-6">
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Add a video manually</h2>
          <p className="mt-1 text-xs text-[var(--pv-text-4)]">For videos published before this system existed — job-approved productions appear here automatically.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div><label className={labelCls}>Internal title</label><input data-testid="create-internal-title" required minLength={2} value={createForm.internal_title} onChange={(e) => setCreateForm((f) => ({ ...f, internal_title: e.target.value }))} className={inputCls} /></div>
            <div><label className={labelCls}>Public title</label><input value={createForm.public_title} onChange={(e) => setCreateForm((f) => ({ ...f, public_title: e.target.value }))} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>YouTube URL</label><input data-testid="create-youtube-url" value={createForm.youtube_url} onChange={(e) => setCreateForm((f) => ({ ...f, youtube_url: e.target.value }))} placeholder="https://youtube.com/shorts/..." className={inputCls} /></div>
            {["animator_id", "editor_id", "scriptwriter_id"].map((k) => (
              <div key={k}>
                <label className={labelCls}>{k.replace("_id", "").replace("_", " ") || k}</label>
                <select value={createForm[k]} onChange={(e) => setCreateForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls}>
                  <option value="">— none —</option>
                  {workers.map((w) => <option key={w.id} value={w.id}>{w.name || w.email}</option>)}
                </select>
              </div>
            ))}
          </div>
          <button data-testid="create-video-submit" type="submit" disabled={saving} className="mt-4 flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Add to library
          </button>
        </form>
      )}

      {tab === "unlinked" && (
        <div className="mt-6" data-testid="unlinked-section">
          {unlinked.length === 0 ? (
            <div data-testid="unlinked-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
              <Youtube className="h-10 w-10 text-[var(--pv-text-4)]" />
              <p className="max-w-md px-6 text-center text-sm text-[var(--pv-text-4)]">Every synced YouTube video is linked to a production. New uploads land here after the next sync.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {unlinked.map((v) => (
                <div key={v.youtube_id} data-testid={`unlinked-card-${v.youtube_id}`} className="overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
                  {v.thumbnail && <img src={v.thumbnail} alt="" className="aspect-video w-full object-cover" loading="lazy" />}
                  <div className="p-4">
                    <p className="line-clamp-2 text-sm font-bold text-[var(--pv-text)]">{v.title}</p>
                    <p className="mt-1 text-[11px] font-semibold text-[var(--pv-text-4)]">
                      Published {v.published_at ? new Date(v.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"} · {fmtNum(v.views || 0)} views{v.rpm != null && <> · {fmtMoney(Number(v.rpm), 2)} RPM</>}
                    </p>
                    {v.suggestions?.length > 0 && (
                      <p data-testid={`unlinked-suggestion-${v.youtube_id}`} className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-amber-300">
                        <Sparkles className="h-3 w-3 shrink-0" /> Likely match: {v.suggestions[0].public_title || v.suggestions[0].internal_title}{v.suggestions[0].animator_name ? ` — ${v.suggestions[0].animator_name}` : ""}
                      </p>
                    )}
                    {!readOnly && (
                      <button
                        data-testid={`link-btn-${v.youtube_id}`}
                        onClick={() => setLinkTarget(v)}
                        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cyan-600"
                      >
                        <Link2 className="h-3.5 w-3.5" /> Link to production
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "youtube" && synced.length > 0 && (
        <div className="mt-6" data-testid="youtube-synced-section">
          <p className="mb-2 flex items-center gap-2 font-mono-label text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--pv-text-4)]">
            <Youtube className="h-3.5 w-3.5 text-rose-400" /> Synced from YouTube ({synced.length})
          </p>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
            {synced.map((v) => (
              <button key={v.youtube_id} data-testid={`yt-video-${v.youtube_id}`} onClick={() => setSelSync(v)}
                className="group overflow-hidden rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] text-left transition-all hover:-translate-y-0.5 hover:border-rose-500/40">
                {v.thumbnail && <img src={v.thumbnail} alt="" className="aspect-video w-full object-cover" loading="lazy" />}
                <div className="p-2.5">
                  <p className="line-clamp-2 text-[11px] font-bold text-[var(--pv-text)]">{v.title}</p>
                  <p className="mt-1 text-[10px] font-semibold text-[var(--pv-text-4)]">
                    {(v.views || 0).toLocaleString()} views
                    {v.stats?.engagedViews != null && <> · {((v.stats.engagedViews / (v.views || 1)) * 100).toFixed(0)}% engaged</>}
                    {v.rpm != null && <> · {fmtMoney(Number(v.rpm), 2)} RPM</>}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {tab === "youtube" && synced.length === 0 && (
        <div data-testid="youtube-empty" className="mt-6 flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
          <Youtube className="h-10 w-10 text-[var(--pv-text-4)]" />
          <p className="max-w-md px-6 text-center text-sm text-[var(--pv-text-4)]">Nothing synced yet — connect YouTube above and run a sync.</p>
        </div>
      )}

      {selSync && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setSelSync(null)}>
          <div data-testid="yt-video-modal" className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <p className="font-display text-sm font-bold text-[var(--pv-text)]">{selSync.title}</p>
              <button data-testid="yt-video-modal-close" onClick={() => setSelSync(null)} aria-label="Close" className="rounded-full border border-[var(--pv-border)] p-1.5 text-[var(--pv-text-3)] transition-colors hover:text-[var(--pv-text)]"><X className="h-4 w-4" /></button>
            </div>
            <VideoAnalyticsPanel youtubeUrl={`https://youtu.be/${selSync.youtube_id}`} />
          </div>
        </div>
      )}

      {linkTarget && (
        <LinkToProductionModal yt={linkTarget} readOnly={readOnly} onClose={() => setLinkTarget(null)} onDone={() => { setLinkTarget(null); load(); }} />
      )}

      {showDigest && <DigestPreviewModal onClose={() => setShowDigest(false)} />}

      {tab !== "unlinked" && tab !== "youtube" && (
      <div className="mt-6">
        {selectMode && (
          <div data-testid="bulk-action-bar" className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-cyan-500/25 bg-cyan-500/5 px-4 py-3">
            <p className="text-xs font-bold text-[var(--pv-text)]"><span data-testid="bulk-selected-count">{selectedIds.length}</span> selected — tap cards to {tab === "archived" ? "pick what to restore" : "pick what to archive"}</p>
            <button
              data-testid="bulk-archive-btn"
              onClick={bulkArchive}
              disabled={selectedIds.length === 0 || bulkBusy}
              className="flex items-center gap-1.5 rounded-full bg-slate-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-slate-600 disabled:opacity-50"
            >
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : tab === "archived" ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              {tab === "archived" ? "Restore selected" : "Archive selected"}
            </button>
            <button data-testid="bulk-clear-btn" onClick={() => { setSelectedIds([]); setSelectMode(false); }} className="text-xs font-semibold text-[var(--pv-text-4)] hover:text-[var(--pv-text)]">Clear</button>
          </div>
        )}
        {!videos ? (
          <p data-testid="videos-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading library...</p>
        ) : videos.length === 0 ? (
          <div data-testid="videos-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
            <Film className="h-10 w-10 text-[var(--pv-text-4)]" />
            <p className="max-w-md px-6 text-center text-sm text-[var(--pv-text-4)]">Nothing here yet. Completed job approvals appear automatically — or add published videos manually.</p>
          </div>
        ) : view === "cards" ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v) => {
              const profit = (v.performance?.revenue || 0) - (v.costs?.total || 0);
              const isPicked = selectedIds.includes(v.id);
              return (
                <button key={v.id} data-testid={`video-card-${v.id}`} onClick={() => (selectMode ? toggleSelect(v.id) : openDetail(v.id))} className={`rounded-3xl border bg-[var(--pv-card)] p-5 text-left transition-all hover:-translate-y-0.5 ${selectMode && isPicked ? "border-cyan-400 ring-2 ring-cyan-500/30" : "border-[var(--pv-border)] hover:border-cyan-500/30"}`}>
                  <div className="flex items-start justify-between gap-2">
                    {selectMode ? (
                      <span data-testid={`video-select-${v.id}`} className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${isPicked ? "border-cyan-400 bg-cyan-500 text-white" : "border-[var(--pv-border-strong)] text-transparent"}`}>
                        <Check className="h-3 w-3" />
                      </span>
                    ) : (
                      <Film className="h-5 w-5 shrink-0 text-cyan-400" />
                    )}
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[v.status] || ""}`}>{(v.status || "").replace("_", " ")}</span>
                    {v.archived && <span className="rounded-full border border-slate-500/40 bg-slate-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-300">Archived</span>}
                  </div>
                  <h3 className="mt-3 truncate font-display text-base font-bold text-[var(--pv-text)]">{v.public_title || v.internal_title}</h3>
                  <p className="mt-0.5 truncate text-xs text-[var(--pv-text-4)]">{v.internal_title}</p>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--pv-text-3)]">
                    {v.animator_name && <span>Anim: <span className="font-bold text-[var(--pv-text)]">{v.animator_name}</span></span>}
                    {v.editor_name && <span>Edit: <span className="font-bold text-[var(--pv-text)]">{v.editor_name}</span></span>}
                    {v.scriptwriter_name && <span>Script: <span className="font-bold text-[var(--pv-text)]">{v.scriptwriter_name}</span></span>}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2 border-t border-[var(--pv-border)] pt-3 text-[11px]">
                    {v.youtube_stats ? (
                      <span className="flex min-w-0 items-center gap-1.5 text-[var(--pv-text-4)]" data-testid={`video-pulse-${v.id}`} title={v.youtube_stats.previous?.synced_at ? `vs previous sync ${new Date(v.youtube_stats.previous.synced_at).toLocaleString()}` : "linked — trend appears after the next sync"}>
                        <span className="rounded bg-rose-500/15 px-1 text-[8px] font-bold text-rose-400">YT LIVE</span>
                        <span className="font-semibold text-[var(--pv-text-2)]">{fmtNum(v.youtube_stats.views ?? 0)}</span>
                        <TrendDelta current={v.youtube_stats.views} previous={v.youtube_stats.previous?.views} />
                        <span>·</span>
                        <span className="font-semibold text-[var(--pv-text-2)]">{v.youtube_stats.estimated_revenue == null ? "—" : fmtMoney(v.youtube_stats.estimated_revenue, 2)}</span>
                        <TrendDelta current={v.youtube_stats.estimated_revenue} previous={v.youtube_stats.previous?.estimated_revenue} money />
                        <Sparkline history={v.youtube_stats.history} field="views" />
                      </span>
                    ) : (
                      <span className="text-[var(--pv-text-4)]">{((v.performance?.views) || 0).toLocaleString()} views · {fmtMoney(v.performance?.revenue ?? 0, 0)} rev</span>
                    )}
                    <span className={`shrink-0 font-bold ${profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{profit >= 0 ? "+" : "−"}{fmtMoney(Math.abs(profit), 0)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead>
                <tr className="border-b border-[var(--pv-border)] text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                  <th className="px-5 py-3">Video</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Crew</th><th className="px-4 py-3">Views</th><th className="px-4 py-3">Revenue</th><th className="px-4 py-3">Cost</th><th className="px-4 py-3">Profit</th><th className="px-4 py-3">Published</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((v) => {
                  const profit = (v.performance?.revenue || 0) - (v.costs?.total || 0);
                  return (
                    <tr key={v.id} data-testid={`video-row-${v.id}`} onClick={() => (selectMode ? toggleSelect(v.id) : openDetail(v.id))} className={`cursor-pointer border-b border-[var(--pv-border)] last:border-0 hover:bg-[var(--pv-subtle)] ${selectMode && selectedIds.includes(v.id) ? "bg-cyan-500/10" : ""}`}>
                      <td className="px-5 py-3"><p className="flex items-center gap-2 font-bold text-[var(--pv-text)]">{selectMode && <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${selectedIds.includes(v.id) ? "border-cyan-400 bg-cyan-500 text-white" : "border-[var(--pv-border-strong)] text-transparent"}`}><Check className="h-2.5 w-2.5" /></span>}{v.public_title || v.internal_title}</p><p className="text-[var(--pv-text-4)]">{v.internal_title}</p></td>
                      <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[v.status] || ""}`}>{(v.status || "").replace("_", " ")}</span></td>
                      <td className="px-4 py-3 text-[var(--pv-text-3)]">{[v.animator_name, v.editor_name, v.scriptwriter_name].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-2)]">
                        {((v.youtube_stats?.views ?? v.performance?.views) || 0).toLocaleString()}
                        {v.youtube_stats && <TrendDelta current={v.youtube_stats.views} previous={v.youtube_stats.previous?.views} />}
                      </td>
                      <td className="px-4 py-3 text-[var(--pv-text-2)]">{fmtMoney(v.youtube_stats?.estimated_revenue ?? v.performance?.revenue ?? 0)}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-2)]">{fmtMoney(v.costs?.total || 0)}</td>
                      <td className={`px-4 py-3 font-bold ${profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtMoney(profit)}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-4)]">{v.publish_date ? new Date(v.publish_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
